/*
  Complete Your Routine.

  Selection state, live total, and one bulk add to cart.

  The cart flow deliberately mirrors ProductBoughtTogether.submitBoughtTogether in
  assets/main-product.js: same fetchConfig("json"), same sections payload so the
  drawer re-renders, same cart-count / header-total-price / free-ship updates, same
  cartAction() + open(). If that flow ever changes there, change it here too.

  Not registered if the tag is already defined — the section can render more than
  once in the theme editor.
*/
if (!customElements.get("complete-your-routine")) {
  class CompleteYourRoutine extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll("[data-routine-card]"));
      this.totalEl = this.querySelector("[data-routine-total]");
      this.addBtn = this.querySelector("[data-routine-add]");
      this.addLabelEl = this.addBtn.querySelector("[data-routine-add-label]");
      this.statusEl = this.querySelector("[data-routine-status]");
      this.discount = parseFloat(this.dataset.discount || "0") || 0;
      this.moneyFormat = this.dataset.moneyFormat || "{{amount}}";

      this.cards.forEach((card) => this.bindCard(card));
      this.addBtn.addEventListener("click", this.onAdd.bind(this));
      document.addEventListener("click", this.onDocumentClick.bind(this));
      this.sync();
    }

    bindCard(card) {
      const input = card.querySelector("[data-routine-check]");
      const body = card.querySelector("[data-routine-toggle]");
      const trigger = card.querySelector("[data-routine-shade-trigger]");
      const list = card.querySelector("[data-routine-shades]");

      if (input) input.addEventListener("change", () => this.sync());

      if (body) {
        body.addEventListener("click", (event) => {
          // the shade control lives inside the body button; let it win
          if (event.target.closest("[data-routine-shade-trigger]")) return;
          if (input) input.checked = !input.checked;
          this.sync();
        });
      }

      if (trigger && list) {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const open = card.classList.contains("is-open");
          this.closeAllShades();
          if (!open) {
            card.classList.add("is-open");
            list.hidden = false;
          }
        });

        list.querySelectorAll("[data-routine-shade]").forEach((option) => {
          option.addEventListener("click", (event) => {
            event.stopPropagation();
            this.selectShade(card, option);
          });
        });
      }
    }

    selectShade(card, option) {
      const input = card.querySelector("[data-routine-check]");
      const price = card.querySelector("[data-routine-price]");
      const label = card.querySelector("[data-routine-shade-label]");
      const image = card.querySelector("[data-routine-media] img");

      if (input) input.value = option.dataset.variantId;
      if (price) {
        if (option.dataset.priceCents) price.dataset.priceCents = option.dataset.priceCents;
        if (option.dataset.price) price.textContent = option.dataset.price;
      }
      if (label) label.textContent = option.querySelector("span:last-child").textContent.trim();
      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.removeAttribute("srcset");
      }
      this.closeAllShades();
      this.sync();
    }

    closeAllShades() {
      this.cards.forEach((card) => {
        card.classList.remove("is-open");
        const list = card.querySelector("[data-routine-shades]");
        if (list) list.hidden = true;
      });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeAllShades();
    }

    selected() {
      return this.cards.filter((card) => {
        const input = card.querySelector("[data-routine-check]");
        return input && input.checked;
      });
    }

    // Cents come straight from Liquid (`variant.price` is an integer), never from
    // the rendered string. Parsing formatted money is a trap: this shop renders
    // "Rs.2,300", and stripping to [\d.,] leaves ".2300" -> 0.23.
    priceOf(card) {
      const el = card.querySelector("[data-routine-price]");
      if (!el) return 0;
      return parseInt(el.dataset.priceCents, 10) || 0;
    }

    // Mirrors Shopify's own formatMoney, driven by shop.money_format.
    groupNumber(value, decimals, thousands, decimalSep) {
      const parts = value.toFixed(decimals).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
    }

    formatMoney(cents) {
      const value = cents / 100;
      const format = this.moneyFormat || "{{amount}}";
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        switch (name) {
          case "amount_no_decimals":
            return this.groupNumber(value, 0, ",", ".");
          case "amount_with_comma_separator":
            return this.groupNumber(value, 2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return this.groupNumber(value, 0, ".", ",");
          case "amount_with_space_separator":
            return this.groupNumber(value, 2, " ", ",");
          case "amount_no_decimals_with_space_separator":
            return this.groupNumber(value, 0, " ", ",");
          case "amount_with_apostrophe_separator":
            return this.groupNumber(value, 2, "'", ".");
          default:
            return this.groupNumber(value, 2, ",", ".");
        }
      });
    }

    sync() {
      const chosen = this.selected();
      this.cards.forEach((card) => {
        const input = card.querySelector("[data-routine-check]");
        card.classList.toggle("is-selected", !!(input && input.checked));
      });

      const count = chosen.length;
      this.addBtn.disabled = count === 0;

      if (!count) {
        this.totalEl.textContent = this.addBtn.dataset.emptyLabel;
        this.addLabelEl.textContent = this.addBtn.dataset.emptyLabel;
        return;
      }

      const subtotal = chosen.reduce((sum, card) => sum + this.priceOf(card), 0);
      const discounted = Math.round(subtotal * (1 - this.discount / 100));

      this.totalEl.textContent = "";
      const label = document.createElement("span");
      label.className = "pdp-routine__total-label";
      label.textContent = "Total";
      this.totalEl.appendChild(label);

      if (this.discount > 0 && discounted < subtotal) {
        const was = document.createElement("s");
        was.className = "pdp-routine__total-was";
        was.textContent = this.formatMoney(subtotal);
        this.totalEl.appendChild(was);
      }

      const now = document.createElement("b");
      now.className = "pdp-routine__total-now";
      now.textContent = this.formatMoney(this.discount > 0 ? discounted : subtotal);
      this.totalEl.appendChild(now);

      this.addLabelEl.textContent =
        this.addBtn.dataset.addLabel + " " + count + " item" + (count > 1 ? "s" : "");
    }

    onAdd() {
      const chosen = this.selected();
      if (!chosen.length) return;

      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");

      const formData = new FormData();
      chosen.forEach((card, index) => {
        const input = card.querySelector("[data-routine-check]");
        formData.append("items[" + index + "][id]", input.value);
        formData.append("items[" + index + "][quantity]", 1);
      });

      if (cart && typeof cart.getSectionsToRender === "function") {
        formData.append(
          "sections",
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
      }

      const config = fetchConfig("json");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      this.addBtn.classList.add("loading");
      this.addBtn.disabled = true;

      fetch(routes.cart_add_url + ".js", config)
        .then((response) => response.text())
        .then((state) => {
          const parsed = JSON.parse(state);
          if (parsed.status) throw new Error(parsed.description || parsed.message);

          this.refreshCartCounts();

          if (cart && typeof cart.getSectionsToRender === "function" && parsed.sections) {
            cart.getSectionsToRender().forEach((section) => {
              const target = document.getElementById(section.id);
              const markup = parsed.sections[section.id];
              if (!target || !markup) return;
              const html = new DOMParser().parseFromString(markup, "text/html");
              const replacement = html.querySelector("#minicart-form");
              if (replacement) target.innerHTML = replacement.innerHTML;
            });
            if (typeof cart.cartAction === "function") cart.cartAction();
          }

          this.uncheckAll();
        })
        .catch((error) => {
          this.statusEl.textContent =
            "Could not add these to your bag. Please try again.";
          console.error("[complete-your-routine]", error);
        })
        .finally(() => {
          this.addBtn.classList.remove("loading");
          this.sync();
          if (cart && typeof cart.open === "function") cart.open();
        });
    }

    uncheckAll() {
      this.cards.forEach((card) => {
        const input = card.querySelector("[data-routine-check]");
        if (input) input.checked = false;
      });
      this.statusEl.textContent = "";
    }

    refreshCartCounts() {
      fetch("/cart.json")
        .then((res) => res.json())
        .then((cart) => {
          if (cart.item_count === undefined) return;
          document.querySelectorAll(".cart-count").forEach((el) => {
            if (el.classList.contains("cart-count-drawer")) {
              el.innerHTML = "(" + cart.item_count + ")";
            } else {
              el.innerHTML = cart.item_count > 100 ? "~" : cart.item_count;
            }
          });
          const totalPrice = document.querySelector("header-total-price");
          if (totalPrice) totalPrice.updateTotal(cart);
          const freeShip = document.querySelector("free-ship-progress-bar");
          if (freeShip) freeShip.init(cart.items_subtotal_price);
        })
        .catch(() => {});
    }
  }

  customElements.define("complete-your-routine", CompleteYourRoutine);
}
